/**
 * behaviour-analytics.ts — answers "where do people click, how long do they stay, which field
 * slows the wizard down, and do they change template?" without hand-instrumenting every element.
 *
 * Privacy rules (DPDP): NEVER send what a user typed. Field events carry the field's NAME only.
 * Click events on public marketing pages carry visible button/heading text (not user content);
 * anything inside `[data-ga-ignore]` is skipped entirely.
 *
 * Analytics must never break the app: every storage access and tracking call here is guarded.
 * All events go through trackEvent() so they no-op when GA4 is off (localhost, previews, blocked).
 * Adding/renaming a parameter here means updating docs/analytics/GA4_SETUP.md + ga4-definitions.json.
 */
import { trackEvent, isAnalyticsActive } from './analytics';

// ── Pure helpers (unit-tested) ─────────────────────────────────────────────

/**
 * Lower-case, hyphenated, safe for a GA4 parameter value (max 40 chars). Keeps any-script letters,
 * combining marks and digits (Devanagari etc. — this is an Indian product), drops punctuation/emoji.
 */
export function slug(input: string, max = 40): string {
  return input
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}\s_-]/gu, '') // \p{M}: Devanagari vowel signs etc. are combining marks
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, max);
}

/** 3x3 grid position of a click inside the viewport: r0c0 (top-left) … r2c2 (bottom-right). */
export function clickZone(x: number, y: number, width: number, height: number): string {
  const col = Math.min(2, Math.max(0, Math.floor((x / Math.max(1, width)) * 3)));
  const row = Math.min(2, Math.max(0, Math.floor((y / Math.max(1, height)) * 3)));
  return `r${row}c${col}`;
}

const INTERACTIVE = 'a,button,[role="button"],input,select,textarea,summary,label,[data-track]';

/** Which named part of the page an element sits in (data-section, else nav/footer, else 'page'). */
export function sectionOf(el: Element): string {
  const tagged = el.closest('[data-section]');
  if (tagged) return tagged.getAttribute('data-section') || 'page';
  if (el.closest('header,nav')) return 'nav';
  if (el.closest('footer')) return 'footer';
  return 'page';
}

function labelOf(el: Element): string {
  const own =
    el.getAttribute('data-track') ||
    el.getAttribute('id') ||
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    (el.textContent ?? '').trim();
  return slug(own);
}

export interface ClassifiedClick {
  kind: 'click' | 'dead';
  elementId: string;
  section: string;
}

/**
 * Interactive target -> a real click on `elementId`.
 * Anything else -> a dead click, labelled by the nearest short visible text (e.g. a card title),
 * which is exactly the "it looks clickable but is not" signal. Returns null for clicks we ignore
 * (opted out with data-ga-ignore, or on the bare page background).
 *
 * Known limit: a `div` with a React onClick but no role="button" is indistinguishable from a dead
 * element here. Give such controls `data-track="…"` (or role="button") on tracked pages.
 */
export function classifyClick(target: Element): ClassifiedClick | null {
  if (target.closest('[data-ga-ignore]')) return null;
  if (target === document.body || target === document.documentElement) return null;
  const section = sectionOf(target);
  const interactive = target.closest(INTERACTIVE);
  if (interactive) {
    return { kind: 'click', elementId: labelOf(interactive) || interactive.tagName.toLowerCase(), section };
  }
  let node: Element | null = target;
  for (let depth = 0; node && depth < 5; depth += 1, node = node.parentElement) {
    // Only read text from small subtrees — textContent on a big ancestor is O(subtree) per click.
    if (node.children.length > 4) continue;
    const text = (node.textContent ?? '').trim();
    if (text.length > 0 && text.length <= 60) {
      const id = slug(text);
      if (id) return { kind: 'dead', elementId: id, section };
    }
  }
  return { kind: 'dead', elementId: target.tagName.toLowerCase(), section };
}

/**
 * Accumulates time only while the page is visible and the element is in view — a backgrounded tab
 * must not look like "engaged for 20 minutes". Clock is injectable for tests.
 */
export class ActiveTimer {
  private total = 0;
  private since: number | null = null;
  constructor(private readonly now: () => number = () => performance.now()) {}
  start(): void {
    if (this.since === null) this.since = this.now();
  }
  stop(): void {
    if (this.since !== null) {
      this.total += this.now() - this.since;
      this.since = null;
    }
  }
  /** Seconds accumulated so far (running time included). */
  seconds(): number {
    const running = this.since === null ? 0 : this.now() - this.since;
    return (this.total + running) / 1000;
  }
  reset(): void {
    this.total = 0;
    if (this.since !== null) this.since = this.now();
  }
}

const MAX_DURATION_SEC = 1800; // a forgotten tab must not skew averages

export function roundedSeconds(sec: number): number {
  return Math.min(MAX_DURATION_SEC, Math.max(0, Math.round(sec)));
}

/** Run analytics code that must never throw into the app. */
function safely<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

// ── Public marketing pages: clicks, scroll depth, section dwell ────────────

/** Routes covered by the public-page tracker. Extend when a new marketing page ships. */
export const PUBLIC_TRACKED_PATHS = ['/', '/features', '/designs', '/pricing', '/company'];

const MAX_DEAD_CLICKS_PER_PAGE = 30; // a rage-clicking bot must not flood the property
const MAX_CLICK_EVENTS_PER_PAGE = 100; // hard ceiling across click + dead click
const MIN_DWELL_SEC = 2;
const SCROLL_STEPS = [25, 50, 75, 100];
/**
 * A section counts as "in view" at 40% of itself OR 40% of the viewport (tall sections on phones).
 * Limit: a section taller than ~25 viewports never crosses an observer threshold at 40% of the viewport;
 * add a smaller threshold below if such a region ever gets a data-section.
 */
const IN_VIEW_RATIO = 0.4;

/**
 * Installs click / scroll-depth / section-dwell tracking for the current public page.
 * Returns a cleanup that also flushes pending dwell time. Safe to call when GA4 is off (no-op).
 */
export function installPublicPageTracking(): () => void {
  if (typeof window === 'undefined' || !isAnalyticsActive()) return () => undefined;

  const pageType = slug(window.location.pathname === '/' ? 'home' : window.location.pathname);
  let deadClicks = 0;
  let clickEvents = 0;

  const onClick = (e: MouseEvent) =>
    safely(() => {
      if (!(e.target instanceof Element) || clickEvents >= MAX_CLICK_EVENTS_PER_PAGE) return;
      const classified = classifyClick(e.target);
      if (!classified) return;
      const { kind, elementId, section } = classified;
      if (kind === 'dead') {
        if (deadClicks >= MAX_DEAD_CLICKS_PER_PAGE) return;
        deadClicks += 1;
      }
      clickEvents += 1;
      const zone = clickZone(e.clientX, e.clientY, window.innerWidth, window.innerHeight);
      trackEvent(kind === 'dead' ? 'DEAD_CLICK' : 'PUBLIC_CLICK', pageType, elementId, undefined, {
        element_id: elementId,
        section,
        click_zone: zone,
      });
    }, undefined);
  document.addEventListener('click', onClick, true);

  // Scroll depth — GA4's built-in `scroll` event only fires once, at 90%.
  const fired = new Set<number>();
  let ticking = false;
  const checkScroll = () => {
    ticking = false;
    const doc = document.documentElement;
    const pct = ((window.scrollY + window.innerHeight) / Math.max(1, doc.scrollHeight)) * 100;
    for (const step of SCROLL_STEPS) {
      if (pct >= step - 1 && !fired.has(step)) {
        fired.add(step);
        trackEvent('SCROLL_DEPTH', pageType, String(step), undefined, { scroll_percent: String(step) });
      }
    }
  };
  const onScroll = () => {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(() => safely(checkScroll, undefined));
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  // Section dwell — visible time per [data-section], flushed on leave/hide.
  const timers = new Map<string, ActiveTimer>();
  const inView = new Set<string>();
  const observed = new Set<Element>();
  const timerFor = (name: string) => {
    let t = timers.get(name);
    if (!t) timers.set(name, (t = new ActiveTimer()));
    return t;
  };
  const io =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const name = entry.target.getAttribute('data-section');
              if (!name) continue;
              const visibleEnough =
                entry.isIntersecting &&
                (entry.intersectionRatio >= IN_VIEW_RATIO ||
                  entry.intersectionRect.height >= IN_VIEW_RATIO * window.innerHeight);
              if (visibleEnough) {
                inView.add(name);
                if (document.visibilityState === 'visible') timerFor(name).start();
              } else {
                inView.delete(name);
                timerFor(name).stop();
              }
            }
          },
          // Many thresholds so tall sections (whose ratio stays small) still get re-evaluated.
          { threshold: [0, 0.05, 0.1, 0.2, 0.3, 0.4, 0.5, 0.7] },
        );
  const scan = () => {
    document.querySelectorAll('[data-section]').forEach((el) => {
      if (!observed.has(el)) {
        observed.add(el);
        io?.observe(el);
      }
    });
  };
  // Sections may render after a lazy chunk loads — scan twice.
  const scanTimers = [window.setTimeout(scan, 300), window.setTimeout(scan, 2500)];

  const flushDwell = () =>
    safely(() => {
      timers.forEach((timer, name) => {
        const sec = roundedSeconds(timer.seconds());
        timer.reset();
        if (sec >= MIN_DWELL_SEC) {
          trackEvent('SECTION_DWELL', pageType, name, undefined, { section: name, duration_sec: sec });
        }
      });
    }, undefined);
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      timers.forEach((t) => t.stop());
      flushDwell();
    } else {
      inView.forEach((name) => timerFor(name).start());
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', flushDwell);

  return () => {
    scanTimers.forEach((t) => window.clearTimeout(t));
    document.removeEventListener('click', onClick, true);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', flushDwell);
    timers.forEach((t) => t.stop());
    flushDwell();
    io?.disconnect();
  };
}

// ── Wizard: per-run stats shared with the publish event ────────────────────

const WIZ_START_KEY = 'ic_wiz_started_at';
const WIZ_ACTIVE_KEY = 'ic_wiz_active_at';
const WIZ_CHANGES_KEY = 'ic_wiz_template_changes';
const WIZ_LAST_TEMPLATE_KEY = 'ic_wiz_last_template';
/** A run with no activity for this long is abandoned: the next visit starts a fresh run. */
export const WIZARD_IDLE_MS = 30 * 60 * 1000;

function store(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null; // private mode / blocked storage — analytics must never throw
  }
}

/** Storage access that can also throw on write (quota / Safari private mode). */
function withStore<T>(fn: (s: Storage) => T, fallback: T): T {
  const s = store();
  return s ? safely(() => fn(s), fallback) : fallback;
}

export const wizardSession = {
  /**
   * Mark the start of a wizard run. Returns true for a fresh run; false when resuming a run that was
   * active within the last 30 minutes (re-mount, StrictMode, step navigation). A stale marker from an
   * abandoned run is discarded so it cannot inflate `wizard_total_sec`.
   */
  start(now: number = Date.now()): boolean {
    return withStore((s) => {
      const startedAt = Number(s.getItem(WIZ_START_KEY) || 0);
      const activeAt = Number(s.getItem(WIZ_ACTIVE_KEY) || startedAt);
      if (startedAt && now - activeAt < WIZARD_IDLE_MS) return false;
      [WIZ_CHANGES_KEY, WIZ_LAST_TEMPLATE_KEY].forEach((k) => s.removeItem(k));
      s.setItem(WIZ_START_KEY, String(now));
      s.setItem(WIZ_ACTIVE_KEY, String(now));
      return true;
    }, false);
  },
  /** Record activity so an in-progress run is not mistaken for an abandoned one. */
  touch(now: number = Date.now()): void {
    withStore((s) => (s.getItem(WIZ_START_KEY) ? s.setItem(WIZ_ACTIVE_KEY, String(now)) : undefined), undefined);
  },
  /**
   * Record the template the user ACTUALLY chose and return the one they chose before (or null).
   * The wizard pre-selects a default template on mount, so `data.templateId` alone cannot tell a
   * deliberate change from the first pick.
   */
  noteSelection(templateId: string): string | null {
    return withStore((s) => {
      const previous = s.getItem(WIZ_LAST_TEMPLATE_KEY);
      s.setItem(WIZ_LAST_TEMPLATE_KEY, templateId);
      return previous;
    }, null);
  },
  recordTemplateChange(): number {
    return withStore((s) => {
      const next = Number(s.getItem(WIZ_CHANGES_KEY) || 0) + 1;
      s.setItem(WIZ_CHANGES_KEY, String(next));
      return next;
    }, 0);
  },
  /**
   * Read-and-clear when an invitation is published so the next run starts clean. Pass the published
   * invitation's `templateId`: if it differs from the template this run chose, the publish did not
   * come from this wizard run (e.g. republishing from the dashboard) and nothing is attached/cleared.
   */
  consume(
    now: number = Date.now(),
    publishedTemplateId?: string,
  ): { wizard_total_sec: number; template_changes: number } | null {
    return withStore((s) => {
      const startedAt = Number(s.getItem(WIZ_START_KEY) || 0);
      if (!startedAt) return null;
      const last = s.getItem(WIZ_LAST_TEMPLATE_KEY);
      if (publishedTemplateId && last && last !== publishedTemplateId) return null;
      const result = {
        wizard_total_sec: roundedSeconds((now - startedAt) / 1000),
        template_changes: Number(s.getItem(WIZ_CHANGES_KEY) || 0),
      };
      [WIZ_START_KEY, WIZ_ACTIVE_KEY, WIZ_CHANGES_KEY, WIZ_LAST_TEMPLATE_KEY].forEach((k) => s.removeItem(k));
      return result;
    }, null);
  },
};

/**
 * Call when a template choice is actually applied. Emits TEMPLATE_CHANGED only if the user had
 * deliberately picked a different template earlier in this run; also feeds `template_changes`,
 * which is attached to INVITATION_PUBLISHED. Never throws (it runs inside a click handler).
 */
export function trackTemplateChange(toTemplateId: string): void {
  safely(() => {
    const fromTemplateId = wizardSession.noteSelection(toTemplateId);
    if (!fromTemplateId || fromTemplateId === toTemplateId) return;
    wizardSession.recordTemplateChange();
    trackEvent('TEMPLATE_CHANGED', 'wizard', toTemplateId, undefined, {
      from_template_id: fromTemplateId,
      to_template_id: toTemplateId,
      template_id: toTemplateId,
    });
  }, undefined);
}

// ── Wizard: time per field (name only, never the value) ────────────────────

const SKIP_FIELD_TYPES = new Set(['password', 'hidden', 'file', 'checkbox', 'radio', 'submit', 'button']);
/** React `useId()` style ids (":r1:", "r3") carry no meaning and are unstable across renders. */
const GENERATED_ID = /^:?r[0-9a-z]{0,4}:?$/i;

/** Field identity from attributes only — never `.value`. Returns null for fields we ignore. */
export function fieldName(el: Element): string | null {
  if (!(el instanceof HTMLElement)) return null;
  if (!el.matches('input,textarea,select,[contenteditable="true"]')) return null;
  if (el.closest('[data-ga-ignore]')) return null;
  if (el instanceof HTMLInputElement && SKIP_FIELD_TYPES.has(el.type)) return null;
  const id = el.id && !GENERATED_ID.test(el.id) ? el.id : '';
  const labelText = (el as HTMLInputElement).labels?.[0]?.textContent ?? '';
  const name = slug(
    el.getAttribute('name') ||
      el.getAttribute('data-field') ||
      el.getAttribute('aria-label') ||
      labelText ||
      id ||
      el.getAttribute('placeholder') ||
      '',
  );
  return name || 'unnamed-field';
}

export type FieldTimingStop = (() => void) & { flush: () => void };

/**
 * Times how long each wizard field holds focus. A field event is sent on blur (or when the step
 * changes / page hides) if it was focused >= 1s of visible time. Call `.flush()` when the step
 * changes: browsers do not fire `focusout` when a focused input is simply removed from the DOM.
 */
export function installWizardFieldTiming(getStepName: () => string): FieldTimingStop {
  const noop = Object.assign(() => undefined, { flush: () => undefined }) as FieldTimingStop;
  if (typeof window === 'undefined' || !isAnalyticsActive()) return noop;

  let current: { name: string; step: string; timer: ActiveTimer } | null = null;

  const flush = () =>
    safely(() => {
      if (!current) return;
      current.timer.stop();
      const sec = roundedSeconds(current.timer.seconds());
      if (sec >= 1) {
        trackEvent('WIZARD_FIELD_TIME', 'wizard', current.name, undefined, {
          step_name: current.step,
          field_name: current.name,
          duration_sec: sec,
        });
      }
      current = null;
    }, undefined);
  const onFocusIn = (e: FocusEvent) =>
    safely(() => {
      flush();
      const name = e.target instanceof Element ? fieldName(e.target) : null;
      if (!name) return;
      current = { name, step: getStepName(), timer: new ActiveTimer() };
      if (document.visibilityState === 'visible') current.timer.start();
    }, undefined);
  const onVisibility = () => {
    if (current && document.visibilityState === 'hidden') {
      current.timer.stop();
      flush();
    }
  };
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', flush);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', flush);
  return Object.assign(
    () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      flush();
    },
    { flush },
  ) as FieldTimingStop;
}
